import { Icon } from '@/design-system/components/icons/Icon';
import { H5, H6, TextBody, TextTitle } from '@/design-system/components/typography';
import { useNavigation } from '@react-navigation/native';
import { ActivityIndicator, Pressable, ScrollView, SxProp, View } from 'dripsy';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStore } from 'store/store';
import { useGetCalcFee } from '../../hooks/useGetCalcFee.hook';
import { useSelectBrokers } from '../../hooks/useSelectBrokers.hook';
import ExchangeImageBackground from '../background/exchange-image-background';
import { SelectExchangeItemNative } from './SelectExchangeItemNative.component';

export const SelectExchangeNativeScreen: React.FC = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const selectBrokers = useSelectBrokers();
  const calcFee = useGetCalcFee({ enabledFetchAppFee: false, enabledFetchMonthlyFee: false });
  const { t } = useTranslation('exchange-sub-account');
  const { container, safeView, header, textHeader, body, content, exchangeTitle, exchangeDescription } = sxStyles;
  const store = useStore();
  const [showView, setShowView] = useState(true);
  const [updateLayout, setUpdateLayout] = useState(true);
  const { width, height } = useWindowDimensions();

  useEffect(() => {
    if (Platform.OS === 'web') {
      const popStateListener = (event: any) => {
        setShowView(false);
        setTimeout(() => setShowView(true), 100);
      };
      window.addEventListener('popstate', popStateListener, false);

      return () => window.removeEventListener('popstate', popStateListener);
    }
  }, []);

  useEffect(() => {
    selectBrokers.getBrokers.refetch();
    calcFee.applicationData.refetch();
    calcFee.monthlyData.refetch();
  }, []);

  useEffect(() => {
    setShowView(!showView);
    const timeout = setTimeout(() => setShowView(true), 200);
    return () => clearTimeout(timeout);
  }, [updateLayout]);

  const onResult = () => {
    setUpdateLayout(!updateLayout);
  };

  if (!showView) return <View />;
  return (
    <View sx={{ ...container, ...{ width, height } }}>
      <ExchangeImageBackground />
      <ScrollView sx={safeView} contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        {Platform.OS !== 'web' && (
          <View sx={{ ...header, ...{ pt: insets.top + 20 } }}>
            <H6 semiBold sx={textHeader}>
              {t('exchange-account::overview.title--exchange-account')}
            </H6>
            <Pressable onPress={() => navigation.goBack()}>
              <Icon color="white" name="closeCircle" width={20} height={20} />
            </Pressable>
          </View>
        )}
        <View sx={body}>
          <View sx={content}>
            <H5 sx={exchangeTitle} bold>
              {t('exchange-account::glossary.select-exchange')}
            </H5>
            <TextTitle semiBold sx={exchangeDescription}>
              {t('exchange-account::landing-page.text--get-connected')}
            </TextTitle>
            <View sx={sxStyles.gridView}>
              {selectBrokers.getBrokers.isLoading && <ActivityIndicator size={'large'} />}
              {!selectBrokers.getBrokers.isLoading && selectBrokers.getBrokers.brokers.length === 0 && (
                <TextBody bold sx={{ color: '$danger' }}>
                  {t('exchange-account::zero-state.title--your-portfolio-is-empty')}
                </TextBody>
              )}
              {selectBrokers.getBrokers.brokers.map((value: any, index: number) => {
                return <SelectExchangeItemNative data={value} calcFee={calcFee} key={index.toString()} onResult={onResult} />;
              })}
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: '$backgroundPrimary'
  },
  safeView: {
    flex: 1,
    flexDirection: 'column'
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    pl: '$4',
    pt: '$3',
    pr: '$4',
    pb: '$3'
  },
  textHeader: {
    color: '$textDarkBackground'
  },
  body: {
    flexDirection: 'column',
    flex: 1
  },
  background: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: '$0',
    top: '$0',
    right: '$0',
    bottom: '$0'
  },
  exchangeTitle: {
    color: '$textTitle',
    textAlign: 'center'
  },
  content: {
    zIndex: 99,
    ml: '$4',
    mt: '$10',
    mr: '$4'
  },
  exchangeDescription: {
    color: '$textDarkBackground',
    mt: '$2',
    textAlign: 'center',
    mb: '$10'
  }
};
